"use client";
import factory from "tiny-relative-date/lib/factory";
import Image from "next/image";
import Link from "next/link";
import { Suspense, useEffect, useState } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import { server, trdLocale, blurImageURL } from "@std/lib/publicEnv.js";
import * as fns from "duration-fns";
import { formatTime } from "@std/lib/mathsExtension.js";
import { Sort } from "./Sort";
import { stringify } from "qs";
import { create } from "@mui/material/styles/createTransitions";
import { set } from "nprogress";
import { useInfiniteQuery } from "@tanstack/react-query";
import { fetchContent } from "./Feed.server";

export const Thumbnail = ({
  id,
  title,
  thumbnail,
  carouselItem,
  favourite,
  views,
  timestamp,
  duration,
}) => {
  let modTranslate = trdLocale;
  modTranslate.overAYearAgo = `${
    new Date().getFullYear() - new Date(timestamp).getFullYear()
  } years ago`;

  const relativeDate = factory(modTranslate)(timestamp);
  const formattedDuration = formatTime(fns.toSeconds(duration));

  const videoQuals = [
    "maxresdefault",
    "hqdefault",
    "sddefault",
    "mqdefault",
    "default",
  ];

  const [videoSrc, setVideoSrc] = useState(
    `https://i3.ytimg.com/vi_webp/${id}`
  );
  const [videoQual, setVideoQual] = useState(videoQuals[0]);
  const [forceUniformSize, setForceUniformSize] = useState(false);

  const formattedViews = new Intl.NumberFormat("en-GB", {
    notation: "compact",
    compactDisplay: "short",
  }).format(parseInt(views || 0));

  return (
    <div className="flex flex-col gap-y-3 w-full">
      <Link
        href={`https://youtube.com/watch?v=${id}`}
        target="_blank"
        rel="noreferrer noopener"
        className="group relative w-full rounded-2xl overflow-hidden"
      >
        <Image
          src={`${videoSrc}/${videoQual}.webp`}
          placeholder="blur"
          loading="lazy"
          blurDataURL={blurImageURL?.video}
          width="1280"
          height="720"
          alt={title || "Unknown title thumbnail"}
          className={`object-cover ${
            forceUniformSize
              ? `aspect-video ${
                  carouselItem ? "2xl:h-[10rem]" : "2xl:h-[10.5rem]"
                } w-full`
              : "w-full h-full"
          } bg-gray-500 text-gray-500`}
          style={{ backgroundPosition: "center" }}
          unoptimized={true}
          onLoad={(i) => {
            if (
              i?.target?.naturalWidth === 1280 &&
              i?.target?.naturalHeight === 720
            )
              return;
            setForceUniformSize(true);
            const cur = videoQuals.indexOf(videoQual);
            const use = cur !== -1 ? cur : null;

            if (use === null) return setVideoSrc(`/thumbnail.jpg`);
            return setVideoQual(videoQuals[cur + 1] || "default");
          }}
        />
        <div className="swiper-lazy-preloader"></div>
        <div className="transition-all ease-in-out duration-200 absolute top-0 left-0 z-[10] bg-opacity-0 group-hover:bg-opacity-[0.15] bg-black w-full h-full" />
        <div className="items-center absolute w-fit mx-2 my-2 py-1 px-1 bottom-0 left-0 bg-gray-800 text-white text-xs text-center leading-4 rounded-md z-20">
          {formattedDuration}
        </div>
        {forceUniformSize ? (
          <span className="absolute w-fit mx-2 my-2 py-1 px-1 top-0 right-0 bg-red-950 text-white text-xs text-center leading-4 rounded-[0.5rem] z-20">
            HD unavailable
          </span>
        ) : null}
      </Link>
      <Link
        href={`https://youtube.com/watch?v=${id}`}
        target="_blank"
        rel="noreferrer noopener"
        className="group text-sm flex flex-col gap-y-0"
      >
        <span className="font-semibold hover:text-gray-300">{title}</span>
        <div className="flex flex-row items-center gap-x-2 text-gray-200">
          <span className="font-medium">
            <b>{formattedViews}+</b> views
          </span>
          <div className="w-1 h-1 rounded-full bg-gray-200" />
          <span className="font-medium">{relativeDate}</span>
        </div>
      </Link>
    </div>
  );
};

export const Loading = () => {
  return (
    <div className="flex flex-col gap-y-3 animate-pulse">
      <Image
        src="/unloaded.png"
        alt="Loading thumbnail"
        width="1280"
        height="720"
        className={`object-cover w-full h-full rounded-xl bg-gray-500`}
        style={{ backgroundPosition: "center" }}
      />
      <div className="flex flex-col gap-y-2">
        <div className="flex flex-row gap-x-2 items-start">
          <Image
            alt="Loading avatar"
            width="64"
            height="64"
            referrerPolicy="no-referrer"
            className="hidden rounded-xl w-12 h-12"
            src={"/unloaded.png"}
            unoptimized
          />
          <span className="font-BreezeAltHeader font-medium text-sm flex flex-col gap-y-2">
            <div className="w-48 h-4 sm:w-12 md:w-16 lg:w-20 2xl:w-48 bg-gray-500 rounded-full" />
            <div className="flex flex-row gap-x-2 font-normal">
              <div className="w-24 h-4 sm:w-8 md:w-12 lg:w-16 2xl:w-24 bg-gray-500 rounded-full" />
            </div>
          </span>
        </div>
      </div>
    </div>
  );
};

const typeTable = {
  video: "video",
  short: "short",
};

export const Feed = ({
  initial,
  initialMore,
  initialState,
  total,
  client,
  type = "video",
}) => {
  const [videos, setVideos] = useState(initial.map((v) => v._source));
  const [length, setLength] = useState(initial.length);
  const [more, setMore] = useState(initialMore || true);
  const [state, setState] = useState({
    sort: "newest-first",
    favourites: false,
  });

  const getter = async (opts) => {
    let selected = opts?.reset ? opts?.newState : state;
    if (opts?.reset) {
      setState(opts?.newState);
      setVideos([]);
      setLength(0);
      setMore(true);
    }

    const determinedType = type === "thumbnail" ? "all" : type;

    const queries = stringify({
      start: opts?.reset ? 0 : videos?.length,
      limit: 40,
      client: client?.id,
      type: determinedType || "all",
      thumbnails: type === "thumbnail" ? "true" : "false",
      sort: selected?.sort || "newest-first",
      favourites: selected?.favourites || false,
    });

    const { hits } = await fetch(`${server}/api/content/search/?${queries}`)
      .then((r) => r.json())
      .catch((e) => ({
        hits: {
          total: 0,
          relation: "eq",
          max_score: null,
          hits: [],
        },
      }));

    const newArr = (video) =>
      [...video, ...hits?.hits?.map((v) => v._source)].filter((v) => {
        return true;
      });

    const newLength = opts?.reset
      ? hits?.hits?.map((v) => v._source).length
      : [...videos, ...hits?.hits?.map((v) => v._source)].length;

    setLength(newLength);
    setMore(hits?.total?.value > newLength);

    setVideos(opts?.reset ? hits?.hits?.map((v) => v._source) : newArr);
  };

  const refreshBasedOnState = (newState) => {
    getter({ reset: true, newState });
  };

  const k = [...Array(15).keys()];
  return (
    <div className="flex flex-col gap-y-4">
      <div className="flex flex-row justify-between items-center text-gray-200 w-full">
        <span className="text-lg font-semibold">
          Showing {total?.value} result{length !== 1 && "s"}
        </span>
        <Sort state={state} onChange={refreshBasedOnState} />
      </div>
      <Suspense>
        <InfiniteScroll
          dataLength={length}
          next={getter}
          hasMore={more}
          loader={k.map((i) => (
            <Loading key={i} />
          ))}
          className="grid sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-x-4 gap-y-8"
        >
          {videos?.map((video, key) => (
            <div key={key}>
              <Thumbnail
                id={video?.id}
                thumbnail={video?.social?.thumbnail?.maxres?.url}
                title={video?.title}
                timestamp={video?.timestamp}
                duration={video?.duration}
                views={video?.social?.views}
              />
            </div>
          ))}
        </InfiniteScroll>
      </Suspense>
    </div>
  );
};
